import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { cx } from '@parrot/utils';
import { Tv, Film } from 'lucide-react';
import { formatUsd } from '@/lib/format';
import { Skeleton } from '@/components/primitives';
import type { RankWindowRow } from '@/lib/titleCharts';
import styles from './NeighborRankTable.module.css';

const POSTER_CDN = 'https://cdn-fastly.parrotanalytics.com/images/poster-hyphen';

export interface NeighborRankTableProps {
  heading: string;
  /** Optional info affordance beside the heading (e.g. a Tooltip-wrapped icon). */
  headingHint?: ReactNode;
  rows: RankWindowRow[];
  activeId: string;
  maxRevenue: number;
  scopeLabel?: string;
  loading?: boolean;
  emptyLabel?: string;
}

/**
 * The d360 "Revenue & Rank" side table, rebuilt to the OG spec: large poster,
 * 2-line title with entity icon, a thin bottom-aligned gold revenue bar, the
 * revenue value, and a big Bebas rank number. The active row gets a black
 * background with a gold left-stripe. Real neighbors come from /leaderboard.
 */
export function NeighborRankTable({
  heading,
  headingHint,
  rows,
  activeId,
  maxRevenue,
  scopeLabel,
  loading,
  emptyLabel = 'No ranking data',
}: NeighborRankTableProps) {
  const navigate = useNavigate();

  return (
    <div className={styles.rankings}>
      <div className={styles.rankHeader}>
        <h4 className={styles.rankHeading}>{heading}</h4>
        {scopeLabel && <span className={styles.rankScope}>{scopeLabel}</span>}
        {headingHint}
      </div>

      <div className={styles.colHead}>
        <span>Title</span>
        <span>Revenue &amp; Rank</span>
      </div>

      {loading ? (
        <div aria-busy="true">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className={styles.row}>
              <div className={styles.titleCell}>
                <Skeleton width={42} height={63} />
                <Skeleton width={`${100 - i * 10}px`} height={12} />
              </div>
              <div className={styles.rankCell}>
                <div className={styles.revBlock}>
                  <Skeleton width={64} height={14} />
                  <Skeleton height={4} style={{ marginTop: 8, width: '70%' }} />
                </div>
                <Skeleton width={48} height={30} />
              </div>
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.empty}>{emptyLabel}</div>
      ) : (
        rows.map((row) => {
          const active = row.parrot_id === activeId;
          const isMovie = row.content_type === 'movie';
          return (
            <div
              key={row.parrot_id}
              className={cx(styles.row, active && styles.rowActive)}
              onClick={() => !active && navigate(`/titles/${row.parrot_id}`)}
              role={active ? undefined : 'link'}
              tabIndex={active ? undefined : 0}
              onKeyDown={(e) => {
                if (!active && (e.key === 'Enter' || e.key === ' ')) navigate(`/titles/${row.parrot_id}`);
              }}
              aria-current={active ? 'true' : undefined}
            >
              <div className={styles.titleCell}>
                <img
                  className={styles.poster}
                  src={`${POSTER_CDN}/${row.parrot_id}.jpg?width=84`}
                  alt=""
                  aria-hidden="true"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                  }}
                />
                <span className={cx(styles.displayName, active && styles.displayNameActive)} title={row.title}>
                  {row.title}
                </span>
                <span className={styles.entityIcon} aria-hidden="true">
                  {isMovie ? <Film size={14} /> : <Tv size={14} />}
                </span>
              </div>

              <div className={styles.rankCell}>
                <div className={styles.revBlock}>
                  <span className={styles.revenueValue}>{formatUsd(row.revenue_contribution_usd)}</span>
                  <span className={styles.track}>
                    <span
                      className={styles.fill}
                      style={{
                        width: `${maxRevenue > 0 ? Math.min(1, row.revenue_contribution_usd / maxRevenue) * 100 : 0}%`,
                      }}
                    />
                  </span>
                </div>
                <span className={styles.rank}>
                  <span className={styles.pound}>#</span>
                  {row.title_rank}
                </span>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
